import React, { useMemo, useState } from "react";
import { ChartWrapper, GroupedBarChart, LineChart } from "components/charts";
import {
  getFormattedTimeStringFromPeriod,
  validLineData,
  createDateRange,
  type PerformanceWithAccountFilter,
  noData,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { sharedAccountReportingLineProps } from "features/Reporting/Content/shared";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { useQuery } from "@tanstack/react-query";
import { getTemplateActivityLineChartQuery, getTemplateCardReportingQuery } from "./queries";
import { sharedGoogleReportingBarProps } from "features/Reporting/Google/utils/shared";
import { useGoogleTooltipFormats } from "features/Reporting/Google/utils/useGoogleTooltipFormats";
import styles from "./templateActivityChart.module.scss";
import CardReporting from "components/cards/CardReporting/CardReporting";

export type ChartState = "line" | "column";

interface Props {
  templateId: number;
  filter: PerformanceWithAccountFilter;
}

const handleDropdownChange = (dropdownValue: string, setFunc: (newState: ChartState) => void) => {
  dropdownValue.indexOf("Bar") === -1 ? setFunc("line") : setFunc("column");
};

export const TemplateActivityChart: React.FC<Props> = ({ templateId, filter }) => {
  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);
  const [activityChartState, setActivityChartState] = useState<ChartState>("line");

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const { groupedBarFormatter } = useGoogleTooltipFormats(chartPeriod, dateRange);
  const lineChartFilter = useMemo(() => ({ ...filter, aggregation: chartPeriod }), [filter, chartPeriod]);
  const templateActivityLineChartQuery = useQuery({
    queryKey: ["templateUserActivityLineChart", templateId, lineChartFilter],
    queryFn: getTemplateActivityLineChartQuery,
    enabled: chartPeriod !== CHART_PERIOD.UNSET && lineChartFilter.type !== undefined,
  });

  const templateCardReportingQuery = useQuery({
    queryKey: ["templateUserCardReporting", templateId, filter],
    queryFn: getTemplateCardReportingQuery,
    enabled: filter.type !== undefined,
  });

  const activeUsersChart = useMemo(() => {
    if (!templateActivityLineChartQuery.data) {
      return <></>;
    }
    return activityChartState === "line" ? (
      <LineChart
        {...sharedAccountReportingLineProps}
        xData={templateActivityLineChartQuery.data?.lineData.xData}
        yData={templateActivityLineChartQuery.data?.lineData.yData}
        xFormatterFunc={(d) => getFormattedTimeStringFromPeriod(d, chartPeriod, dateRange)}
        colorRange={[reportingColors.greenDark, reportingColors.blue]}
      />
    ) : (
      <GroupedBarChart
        {...sharedGoogleReportingBarProps}
        data={templateActivityLineChartQuery.data.barData}
        order={templateActivityLineChartQuery.data.order}
        xTickFormatter={(d) => getFormattedTimeStringFromPeriod(d.toLocaleString(), chartPeriod, dateRange)}
        tooltipFormatter={groupedBarFormatter}
        colorRange={[reportingColors.greenDark, reportingColors.blue]}
      />
    );
  }, [templateActivityLineChartQuery.data, activityChartState, chartPeriod, dateRange, groupedBarFormatter]);

  return (
    <>
      <h2 className={`${styles.summaryHeader}`}>Users</h2>
      <div className={styles.activity} ref={measureRef}>
        <ChartWrapper
          titles={["Activity - Line Chart", "Activity - Bar Chart"]}
          legendLabels={["Platform Outreach", "Platform Interactions"]}
          showLegend
          onChange={(option) => handleDropdownChange(option, setActivityChartState)}
          colorRange={[reportingColors.greenDark, reportingColors.blue]}
        >
          <RequestStatusRenderer state={templateActivityLineChartQuery.status}>
            {templateActivityLineChartQuery.isSuccess &&
              (validLineData([
                templateActivityLineChartQuery.data.lineData.yData[0],
                templateActivityLineChartQuery.data.lineData.yData[1],
              ])
                ? activeUsersChart
                : noData(filter))}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={templateCardReportingQuery.status}>
          {templateCardReportingQuery.isSuccess && <CardReporting items={templateCardReportingQuery.data} />}
        </RequestStatusRenderer>
      </div>
    </>
  );
};
